import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Minus, Sparkles, Undo2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ACTIVE_THREAD_STATES,
  type ChatAttachment,
  type DocBlock,
  blockPlainText,
  findBlock,
  isChatMode,
} from '@hub/contracts'
import { orpc, creditsExhaustedReason } from '@/lib/orpc-client'
import { useAuthState } from '@/lib/auth-state'
import { useChatCollab } from '@/lib/collab/use-chat-collab'
import { useBubbleMotion, type BubbleView } from '@/lib/chat/bubble-motion'
import { usePresence } from '@/lib/collab/presence-context'
import { getChatDraftMode, setChatDraft } from '@/lib/chat-draft-store'
import { ChatThread } from '@/components/chat/chat-thread'
import { ChatComposer } from '@/components/chat/chat-composer'
import { addSelectionToDraft, SelectionActionMenu } from '@/components/chat/selection-menu'
import type { ChatSelection } from '@/lib/chat/selections'
import { Button } from '@/components/ui/button'
import { HubBotAvatar, useHubBotPalette, type HubBotPalette } from '@/components/hub-bot-avatar'
import { cn } from '@/lib/utils'

/**
 * The HUB bubble (PLAN-079 §4.2): a round button docked bottom-right of the
 * page, draggable anywhere, that expands into a compact chat panel bound to
 * the thread of what the page shows — a document (the block selection rides
 * along as context, so "make this shorter" edits exactly those blocks) or an
 * inbox email (PLAN-084: the email's own thread, shared with its runs).
 */

const BUBBLE_SIZE = 52
const PANEL_W = 400
const PANEL_H = 560
const MARGIN = 16
const POS_KEY = 'hub-bubble-pos'

interface Pos {
  x: number
  y: number
}

function readPos(): Pos | null {
  try {
    const raw = localStorage.getItem(POS_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as Pos
    return typeof p.x === 'number' && typeof p.y === 'number' ? p : null
  } catch {
    return null
  }
}

function clamp(p: Pos, w: number, h: number, avoidRight = 0): Pos {
  const maxX = Math.max(MARGIN, window.innerWidth - avoidRight - w - MARGIN)
  const maxY = Math.max(MARGIN, window.innerHeight - h - MARGIN)
  return { x: Math.min(Math.max(MARGIN, p.x), maxX), y: Math.min(Math.max(MARGIN, p.y), maxY) }
}

/** What the bubble is about: decides the thread, the agent context and the panel chrome. */
export type HubBubbleContext =
  | {
      kind: 'document'
      documentId: string
      documentTitle?: string
      selectedBlockIds: string[]
      blocks: DocBlock[]
      onClearSelection: () => void
      onSelectBlocks: (ids: string[]) => void
      onJumpToBlock: (id: string) => void
    }
  | {
      kind: 'email'
      emailId: string
      subject: string | null
      /** The email's thread when it already has one (a rule run) — lets the bubble glow before its first open. */
      threadId?: string | null
    }

interface HubBubbleProps {
  projectId: string
  context: HubBubbleContext
  /** Width of a panel docked on the right (comments) the bubble must clear. */
  avoidRight?: number
  /** The view the bubble belongs to — one entrance per view, not per subject. Defaults from the context. */
  view?: BubbleView
}

/** "Skicka till HUB" / "Ask HUB" from anywhere on the page: opens the bubble, optionally on a block with a prompt. */
export const HUB_BUBBLE_ASK_EVENT = 'hub-bubble:ask'
export interface HubBubbleAskDetail {
  blockId?: string
  prompt?: string
  /** Text picked on the page (inbox email body): lands as a quoted selection in the composer. */
  selection?: ChatSelection
  /** Scroll the panel to this message once the thread is in (a run's reply). */
  scrollToMessageId?: string
}

/** Strings per context: the document bubble keeps its `editor` keys, the email bubble uses `inbox`. */
function useBubbleLabels(kind: HubBubbleContext['kind']) {
  const { t: te } = useTranslation('editor')
  const { t: ti } = useTranslation('inbox')
  return kind === 'document'
    ? {
        open: te('blocks.open'),
        collapse: te('blocks.collapse'),
        hint: te('blocks.bubbleHint'),
        title: te('blocks.bubbleTitle'),
        placeholder: te('blocks.bubblePlaceholder'),
      }
    : {
        open: ti('bubble.open'),
        collapse: ti('bubble.collapse'),
        hint: ti('bubble.hint'),
        title: ti('bubble.title'),
        placeholder: ti('bubble.placeholder'),
      }
}

export function HubBubble({ projectId, context, avoidRight = 0, view }: HubBubbleProps) {
  const { t } = useTranslation('editor')
  const labels = useBubbleLabels(context.kind)
  const { me } = useAuthState()
  const [open, setOpen] = useState(false)
  const [threadId, setThreadId] = useState<string | null>(
    context.kind === 'email' ? (context.threadId ?? null) : null
  )
  // The bot: dark on the light theme, light on the dark theme. "Working" comes
  // from the project hub's activity ledger (read-only, already subscribed for
  // the sidebar glow) — so the collapsed bubble glows too, and viewing a page
  // never creates a thread: the id is only known once the panel was opened
  // (or the email already had a run).
  const palette = useHubBotPalette()
  const { activeThreads } = usePresence()
  const busy = threadId !== null && activeThreads.has(threadId)
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null)
  const [pendingSelection, setPendingSelection] = useState<ChatSelection | null>(null)
  const [scrollTarget, setScrollTarget] = useState<string | null>(null)
  const onSelectBlocks = context.kind === 'document' ? context.onSelectBlocks : undefined

  // "Skicka till HUB" (comments, block menu) / "Ask HUB" (inbox): select the
  // block, prefill, open.
  useEffect(() => {
    const onAsk = (e: Event) => {
      const detail = (e as CustomEvent<HubBubbleAskDetail>).detail
      if (detail.blockId) onSelectBlocks?.([detail.blockId])
      if (detail.prompt !== undefined) setPendingPrompt(detail.prompt)
      if (detail.selection) setPendingSelection(detail.selection)
      if (detail.scrollToMessageId) setScrollTarget(detail.scrollToMessageId)
      setOpen(true)
    }
    window.addEventListener(HUB_BUBBLE_ASK_EVENT, onAsk)
    return () => window.removeEventListener(HUB_BUBBLE_ASK_EVENT, onAsk)
  }, [onSelectBlocks])
  useEffect(() => {
    if (!threadId || pendingPrompt === null) return
    setChatDraft(projectId, threadId, pendingPrompt)
    setPendingPrompt(null)
  }, [threadId, pendingPrompt, projectId])
  useEffect(() => {
    if (!threadId || !pendingSelection) return
    addSelectionToDraft(projectId, threadId, pendingSelection)
    setPendingSelection(null)
  }, [threadId, pendingSelection, projectId])
  // Bubble position = bottom-right corner anchor; the panel grows up/left from it.
  const [pos, setPos] = useState<Pos>(() => readPos() ?? { x: -1, y: -1 })
  const dragRef = useRef<{ startX: number; startY: number; origin: Pos; moved: boolean } | null>(
    null
  )
  const [undoneTurns, setUndoneTurns] = useState<Set<string>>(new Set())
  const [undoing, setUndoing] = useState<string | null>(null)

  // Default dock: bottom-right.
  useEffect(() => {
    const place = () => {
      setPos((p) =>
        p.x < 0
          ? {
              x: window.innerWidth - avoidRight - BUBBLE_SIZE - MARGIN * 1.5,
              y: window.innerHeight - BUBBLE_SIZE - MARGIN * 1.5,
            }
          : clamp(p, BUBBLE_SIZE, BUBBLE_SIZE, avoidRight)
      )
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [avoidRight])

  const subjectKey = context.kind === 'document' ? context.documentId : context.emailId

  // Slides in from the nearest viewport edge once the page is up and out
  // again when the view is left (rounds 2–3, BubbleMotionProvider). A drag
  // cancels the entrance; opening the panel or switching subject within the
  // view does not replay it.
  const entrance = useBubbleMotion({
    view: view ?? (context.kind === 'document' ? 'documents' : 'inbox'),
    pos,
    size: BUBBLE_SIZE,
    palette,
    hidden: open,
  })

  // The subject's thread is created lazily on first open (and stays the same
  // thread on reopen/reload — the server keys it by document / email).
  useEffect(() => {
    if (!open || threadId) return
    let cancelled = false
    const request =
      context.kind === 'document'
        ? orpc.chat.getDocumentThread({ projectId, documentId: context.documentId })
        : orpc.chat.getInboxEmailThread({ projectId, emailId: context.emailId })
    request
      .then((thread) => {
        if (!cancelled) setThreadId(thread.id)
      })
      .catch((err) => console.error('[hub-bubble] thread failed:', err))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, threadId, projectId, context.kind, subjectKey])

  // ── drag ──────────────────────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    // Buttons inside the drag handle (minimize) keep their click.
    if (
      (e.target as HTMLElement).closest('button') !== e.currentTarget &&
      (e.target as HTMLElement).closest('button')
    )
      return
    entrance.cancel()
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: pos, moved: false }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    const dx = e.clientX - d.startX
    const dy = e.clientY - d.startY
    if (!d.moved && Math.hypot(dx, dy) < 4) return
    d.moved = true
    setPos(clamp({ x: d.origin.x + dx, y: d.origin.y + dy }, BUBBLE_SIZE, BUBBLE_SIZE, avoidRight))
  }
  const onPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current
    dragRef.current = null
    ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    if (!d) return
    if (d.moved) {
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(pos))
      } catch {
        // ignore
      }
    } else if (!open) {
      setOpen(true)
    }
  }

  // Panel anchored so its bottom-right corner sits at the bubble's bottom-right.
  const panelStyle = useMemo(() => {
    const right = pos.x + BUBBLE_SIZE
    const bottom = pos.y + BUBBLE_SIZE
    const left = Math.max(MARGIN, right - PANEL_W)
    const top = Math.max(MARGIN, bottom - PANEL_H)
    return {
      left,
      top,
      width: Math.min(PANEL_W, window.innerWidth - MARGIN * 2),
      height: Math.min(PANEL_H, window.innerHeight - MARGIN * 2),
    }
  }, [pos])

  return (
    <>
      {open && threadId && (
        <BubblePanel
          style={panelStyle}
          projectId={projectId}
          context={context}
          labels={labels}
          threadId={threadId}
          scrollToMessageId={scrollTarget}
          onScrolledTo={() => setScrollTarget(null)}
          currentUserId={me?.user.id}
          palette={palette}
          undoneTurns={undoneTurns}
          undoing={undoing}
          onUndo={async (turnId) => {
            if (context.kind !== 'document') return
            setUndoing(turnId)
            try {
              await orpc.documents.undoAgentTurn({
                projectId,
                documentId: context.documentId,
                turnId,
              })
              setUndoneTurns((s) => new Set(s).add(turnId))
              toast.success(t('blocks.undone'))
            } catch (err) {
              console.error(err)
              toast.error(t('blocks.undoFailed'))
            } finally {
              setUndoing(null)
            }
          }}
          onCollapse={() => setOpen(false)}
          onDragHandlePointerDown={onPointerDown}
          onDragHandlePointerMove={onPointerMove}
          onDragHandlePointerUp={onPointerUp}
        />
      )}
      <button
        type="button"
        aria-label={open ? labels.collapse : labels.open}
        title={labels.hint}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={cn(
          'fixed z-40 flex items-center justify-center rounded-full select-none',
          open && 'opacity-0 pointer-events-none'
        )}
        data-entrance={entrance.phase === 'done' ? undefined : entrance.phase}
        style={{
          left: pos.x,
          top: pos.y,
          width: BUBBLE_SIZE,
          height: BUBBLE_SIZE,
          touchAction: 'none',
          ...entrance.style,
        }}
      >
        <HubBotAvatar
          palette={palette}
          size={BUBBLE_SIZE}
          working={busy}
          state={entrance.phase === 'landed' ? 'happy' : undefined}
        />
      </button>
    </>
  )
}

interface BubblePanelProps {
  style: React.CSSProperties
  projectId: string
  context: HubBubbleContext
  labels: ReturnType<typeof useBubbleLabels>
  threadId: string
  scrollToMessageId: string | null
  onScrolledTo: () => void
  currentUserId: string | undefined
  palette: HubBotPalette
  undoneTurns: Set<string>
  undoing: string | null
  onUndo: (turnId: string) => Promise<void>
  onCollapse: () => void
  onDragHandlePointerDown: (e: React.PointerEvent) => void
  onDragHandlePointerMove: (e: React.PointerEvent) => void
  onDragHandlePointerUp: (e: React.PointerEvent) => void
}

const NO_BLOCKS: string[] = []

function BubblePanel({
  style,
  projectId,
  context,
  labels,
  threadId,
  scrollToMessageId,
  onScrolledTo,
  currentUserId,
  palette,
  undoneTurns,
  undoing,
  onUndo,
  onCollapse,
  onDragHandlePointerDown,
  onDragHandlePointerMove,
  onDragHandlePointerUp,
}: BubblePanelProps) {
  const { t } = useTranslation('editor')
  const { t: tc } = useTranslation('chat')
  const {
    messages,
    sendMessage,
    discardMessage,
    softDeleteMessage,
    clearAgentError,
    stopAgent,
    deleteMessage,
    typingUsers,
    setTyping,
    ready,
    agentStatus,
    agentBusySince,
    messageQueue,
    enqueueMessage,
    removeQueuedMessage,
    hasOlderMessages,
    loadingOlder,
    loadOlderMessages,
  } = useChatCollab(projectId, threadId)
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const busy = ACTIVE_THREAD_STATES.has(agentStatus.state)

  const document = context.kind === 'document' ? context : null
  const documentId = document?.documentId ?? null
  const selectedBlockIds = document?.selectedBlockIds ?? NO_BLOCKS
  const blocks = document?.blocks
  const panelTitle =
    context.kind === 'document'
      ? (context.documentTitle ?? labels.title)
      : (context.subject ?? labels.title)

  const selectionLabel = useMemo(() => {
    if (!blocks || selectedBlockIds.length === 0) return null
    const titles = selectedBlockIds
      .map((id) => {
        const b = findBlock(blocks, id)
        return b ? blockPlainText(b).slice(0, 28) : null
      })
      .filter((s): s is string => !!s)
    return titles.length ? titles.join(' · ') : null
  }, [selectedBlockIds, blocks])

  const trigger = useCallback(
    async (content: string, attachments?: ChatAttachment[], messageId?: string) => {
      const draftMode = getChatDraftMode(projectId, threadId)
      await orpc.chat.triggerAgent({
        mode: isChatMode(draftMode) ? draftMode : 'normal',
        projectId,
        threadId,
        content,
        messageId,
        attachments: attachments?.map((a) => ({
          id: a.id,
          filename: a.agentFilename ?? a.filename,
          url: a.url,
          size: a.size,
          contentType: a.contentType,
          ...(a.documentId ? { documentId: a.documentId } : {}),
        })),
        // The email needs no per-turn context: the server derives it from
        // the thread (chat.triggerAgent, PLAN-084).
        ...(documentId ? { documentContext: { documentId, blockIds: selectedBlockIds } } : {}),
      })
    },
    [projectId, threadId, documentId, selectedBlockIds]
  )

  const send = useCallback(
    async (message: string, attachments?: ChatAttachment[]) => {
      setTyping(false)
      if (busy) {
        enqueueMessage(message, attachments)
        return
      }
      clearAgentError()
      const id = sendMessage(message, attachments)
      try {
        await trigger(message, attachments, id)
      } catch (err) {
        discardMessage(id)
        const reason = creditsExhaustedReason(err)
        if (reason) {
          toast.error(
            tc(reason === 'project' ? 'credits.exhaustedProject' : 'credits.exhaustedOrg')
          )
          return false
        }
        console.error('[hub-bubble] trigger failed:', err)
      }
    },
    [busy, enqueueMessage, clearAgentError, sendMessage, discardMessage, trigger, setTyping]
  )

  const retry = useCallback(
    (messageId: string) => {
      const idx = messages.findIndex((m) => m.id === messageId)
      const prev = [...messages.slice(0, idx)].reverse().find((m) => m.role === 'user')
      if (!prev) return
      setRetryingMessageId(messageId)
      deleteMessage(messageId)
      clearAgentError()
      trigger(prev.content, prev.attachments, prev.id).catch(console.error)
    },
    [messages, deleteMessage, clearAgentError, trigger]
  )

  // Follow the bottom while streaming.
  useEffect(() => {
    const el = viewportRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, agentStatus.state])

  // Opened on a specific reply (an inbox run): scroll to it once the thread
  // is in. Runs after the bottom-follow above so it wins for that render.
  useEffect(() => {
    if (!ready || !scrollToMessageId) return
    const el = viewportRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(scrollToMessageId)}"]`
    )
    if (!el) return
    el.scrollIntoView({ block: 'start' })
    onScrolledTo()
  }, [ready, scrollToMessageId, messages, onScrolledTo])

  // The card for the document we are in says nothing the page doesn't show;
  // artifacts for *other* documents/tasks stay. (Email threads keep every
  // card — a document HUB filed from the mail is news.)
  const visibleMessages = useMemo(
    () =>
      documentId
        ? messages.map((m) =>
            m.artifacts?.some((a) => a.kind === 'document' && a.id === documentId)
              ? {
                  ...m,
                  artifacts: m.artifacts.filter(
                    (a) => !(a.kind === 'document' && a.id === documentId)
                  ),
                }
              : m
          )
        : messages,
    [messages, documentId]
  )

  // Latest agent turn that touched this document — offers undo.
  const lastDocumentTurn = useMemo(() => {
    if (!documentId) return null
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role !== 'assistant') continue
      const arts = (m.artifacts ?? []).filter(
        (a) => a.kind === 'document' && a.id === documentId && a.turnId
      )
      if (arts.length > 0) {
        const turnId = arts[0].turnId!
        const blockIds = [...new Set(arts.flatMap((a) => a.blockIds ?? []))]
        return { messageId: m.id, turnId, blockIds }
      }
    }
    return null
  }, [messages, documentId])

  const quick = (prompt: string) => {
    void send(prompt)
  }

  return (
    <div
      className="bg-background fixed z-40 flex flex-col overflow-hidden rounded-2xl border shadow-2xl"
      style={style}
    >
      <div
        className="flex h-11 shrink-0 cursor-grab items-center gap-2 border-b px-3 select-none active:cursor-grabbing"
        onPointerDown={onDragHandlePointerDown}
        onPointerMove={onDragHandlePointerMove}
        onPointerUp={onDragHandlePointerUp}
        style={{ touchAction: 'none' }}
      >
        <HubBotAvatar palette={palette} size={24} working={busy} />
        <span className="truncate text-sm font-medium">{panelTitle}</span>
        <span
          className={cn(
            'ml-1 h-2 w-2 rounded-full',
            busy
              ? 'animate-pulse bg-blue-500'
              : agentStatus.state === 'error'
                ? 'bg-destructive'
                : 'bg-emerald-500'
          )}
        />
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto h-7 w-7"
          onClick={onCollapse}
          aria-label={labels.collapse}
        >
          <Minus className="h-4 w-4" />
        </Button>
      </div>

      <div ref={viewportRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-3 py-3">
          {!ready ? (
            <p className="text-muted-foreground py-6 text-center text-xs">{tc('thread.syncing')}</p>
          ) : messages.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-xs">{labels.hint}</p>
          ) : (
            <ChatThread
              messages={visibleMessages}
              projectId={projectId}
              currentUserId={currentUserId}
              agentStatus={agentStatus}
              messageQueue={messageQueue}
              typingUsers={typingUsers}
              hasOlderMessages={hasOlderMessages}
              loadingOlder={loadingOlder}
              onLoadOlder={loadOlderMessages}
              onRetry={retry}
              retryingMessageId={retryingMessageId}
              onDeleteOwnMessage={softDeleteMessage}
              onRemoveQueued={removeQueuedMessage}
            />
          )}
          {document && lastDocumentTurn && !busy && !undoneTurns.has(lastDocumentTurn.turnId) && (
            <div className="bg-muted/60 mt-2 flex flex-wrap items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs">
              <span className="text-muted-foreground mr-1">{t('blocks.changedBlocks')}:</span>
              {lastDocumentTurn.blockIds.slice(0, 4).map((id) => {
                const b = findBlock(document.blocks, id)
                const label = b ? blockPlainText(b).slice(0, 22) || b.type : id
                return (
                  <button
                    key={id}
                    type="button"
                    className="bg-background hover:bg-accent truncate rounded-md border px-1.5 py-0.5"
                    onClick={() => document.onJumpToBlock(id)}
                    title={t('blocks.jumpTo')}
                  >
                    {label}
                  </button>
                )
              })}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 gap-1 px-2 text-xs"
                disabled={undoing === lastDocumentTurn.turnId}
                onClick={() => void onUndo(lastDocumentTurn.turnId)}
              >
                <Undo2 className="h-3 w-3" />
                {t('blocks.undoTurn')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t px-2 pt-2 pb-2">
        {document && selectedBlockIds.length > 0 && (
          <div className="mb-1.5 flex flex-wrap items-center gap-1">
            <span className="bg-accent-soft text-foreground flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px]">
              <Sparkles className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {t('blocks.blocksSelected', { count: selectedBlockIds.length })}
                {selectionLabel ? ` · ${selectionLabel}` : ''}
              </span>
              <button
                type="button"
                onClick={document.onClearSelection}
                aria-label={t('blocks.clearSelection')}
                className="ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground rounded-full border px-2 py-0.5 text-[11px]"
              onClick={() => quick(t('blocks.quickImprovePrompt'))}
            >
              {t('blocks.quickImprove')}
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground rounded-full border px-2 py-0.5 text-[11px]"
              onClick={() => quick(t('blocks.quickSummarizePrompt'))}
            >
              {t('blocks.quickSummarize')}
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground rounded-full border px-2 py-0.5 text-[11px]"
              onClick={() => quick(t('blocks.quickContinuePrompt'))}
            >
              {t('blocks.quickContinue')}
            </button>
          </div>
        )}
        <SelectionActionMenu draftKey={{ projectId, threadKey: threadId }} />
        <ChatComposer
          projectId={projectId}
          onSend={send}
          onTyping={setTyping}
          busy={busy}
          onStop={stopAgent}
          busySince={agentBusySince}
          placeholder={
            document && selectedBlockIds.length > 0
              ? t('blocks.bubbleWithSelection')
              : labels.placeholder
          }
          draftKey={{ projectId, threadKey: threadId }}
          autoFocus
        />
      </div>
    </div>
  )
}
